import { useTranslation } from 'react-i18next'
import { Button, Row, Col, Input, Dropdown, Menu } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGridHorizontal } from '@fortawesome/pro-solid-svg-icons/faGridHorizontal'
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useAppDispatch } from 'store/hooks'
import { slices } from 'store/slices'
import {
  IQuestionBankQuestion,
  IQuestionBankSection,
} from 'interfaces/questionBank'
import {
  DragAndDrop,
  TableHeader,
  TableBody,
  Question,
  Options,
} from './styled'

interface IProps {
  section: IQuestionBankSection
  editQuestion: (data: IQuestionBankQuestion) => void
}

const Questions = ({ section, editQuestion }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const translationPrefix =
    'administration.blocks.type.questionBankV2.sections.questions'

  const onDragEnd = (result: any) => {
    const { destination, source } = result

    if (!destination || destination.index === source.index) {
      return
    }

    const newQuestions = reorder(
      section.questions,
      source.index,
      destination.index
    )
    dispatch(
      slices.questionBank.updateQuestionsOrder({
        sectionOrder: section.order,
        questions: newQuestions,
      })
    )
  }

  const reorder = (
    list: IQuestionBankQuestion[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  return (
    <>
      <TableHeader>
        <Row type="flex">
          <Col style={{ width: 40 }} />
          <Col style={{ flex: 1 }}>
            {t(`${translationPrefix}.columns.question`)}
          </Col>
          <Col style={{ width: 150 }}>
            {t(`${translationPrefix}.columns.questionId`)}
          </Col>
          <Col style={{ width: 110 }}>
            {t(`${translationPrefix}.columns.score`)}
          </Col>
          <Col style={{ width: 32 }} />
        </Row>
      </TableHeader>
      <TableBody>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {section.questions.map((question, index) => (
                  <Draggable
                    key={index}
                    draggableId={index.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{ ...provided.draggableProps.style }}
                      >
                        <Row
                          key={index}
                          type="flex"
                          style={{ backgroundColor: 'white' }}
                        >
                          <Col style={{ width: 40 }}>
                            <DragAndDrop>
                              <Button type="link" {...provided.dragHandleProps}>
                                <FontAwesomeIcon icon={faGridHorizontal} />
                              </Button>
                            </DragAndDrop>
                          </Col>
                          <Col style={{ flex: 1, display: 'grid' }}>
                            <Question>{question.name}</Question>
                          </Col>
                          <Col style={{ width: 150 }}>{question.code}</Col>
                          <Col style={{ width: 110 }}>
                            <Input
                              type="number"
                              min={1}
                              disabled={section.randomOrder}
                              value={question.score}
                              onChange={(event: {
                                target: { value: string }
                              }) => {
                                const value = event.target.value
                                dispatch(
                                  slices.questionBank.changeQuestionScore({
                                    sectionOrder: section.order,
                                    questionOrder: question.order,
                                    score: value === '' ? '' : Number(value),
                                  })
                                )
                              }}
                            />
                          </Col>
                          <Col style={{ width: 32 }}>
                            <Dropdown
                              overlay={
                                <Menu>
                                  <Menu.Item
                                    onClick={() => editQuestion(question)}
                                  >
                                    {t(
                                      `${translationPrefix}.columns.options.edit`
                                    )}
                                  </Menu.Item>
                                  <Menu.Item
                                    onClick={() =>
                                      dispatch(
                                        slices.questionBank.removeQuestion({
                                          sectionOrder: section.order,
                                          questionOrder: question.order,
                                        })
                                      )
                                    }
                                  >
                                    {t(
                                      `${translationPrefix}.columns.options.remove`
                                    )}
                                  </Menu.Item>
                                </Menu>
                              }
                            >
                              <Options>
                                <Button>
                                  <FontAwesomeIcon icon={faEllipsisV} />
                                </Button>
                              </Options>
                            </Dropdown>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </TableBody>
    </>
  )
}

export default Questions
